import {useEffect, useMemo, useRef} from 'preact/hooks';

import {Button} from '~/components/Button';
import {ShopPayIcon} from '~/components/ShopPayIcon';
import {useI18n} from '~/foundation/I18n/hooks';
import {useMonorail} from '~/foundation/Monorail/hooks';
import {RootProvider} from '~/foundation/RootProvider/RootProvider';
import {constructCheckoutLink} from '~/utils/constructCheckoutLink';
import {getHostname} from '~/utils/hostname';
import register from '~/utils/register';
import {isoWindow} from '~/utils/window';

import type {PayButtonProps, PayButtonWebComponentProps} from './types';

const ShopPayButtonInternal = ({
  buttonText,
  channel,
  disabled,
  paymentOption,
  source,
  sourceToken,
  storeUrl,
  variants,
}: PayButtonProps) => {
  const {translate} = useI18n();
  const {getTrekkieAttributes, produceMonorailEvent} = useMonorail();
  const impressionTracked = useRef(false);

  // We need a custom implementation for trackElementImpression which is used by Shop Pay surfaces
  useEffect(() => {
    const trackElementImpression = async () => {
      if (impressionTracked.current) {
        return;
      }

      impressionTracked.current = true;

      const trekkieAttributes = await getTrekkieAttributes(
        'contentLanguage',
        'microSessionCount',
        'microSessionId',
        'referer',
        'shopId',
        'themeCityHash',
        'themeId',
        'uniqToken',
        'visitToken',
      );

      const payload = {
        ...trekkieAttributes,
        elementName: 'shop-pay-button',
        shopJsVersion: '__buildVersionBeta',
      };

      produceMonorailEvent({
        event: {
          schemaId: 'shop_js_ui_impression/1.1',
          payload,
        },
        onError: () => {
          impressionTracked.current = false;
        },
        trekkieAttributes,
      });
    };

    trackElementImpression();
  }, [getTrekkieAttributes, produceMonorailEvent]);

  const hostname = getHostname(storeUrl);

  const href = useMemo(() => {
    if (!hostname || !storeUrl) {
      return;
    }

    return constructCheckoutLink({
      channel,
      paymentOption,
      source,
      sourceToken,
      storeUrl,
      variants,
    });
  }, [
    channel,
    hostname,
    paymentOption,
    source,
    sourceToken,
    storeUrl,
    variants,
  ]);

  const content = useMemo(() => {
    if (buttonText) {
      return buttonText;
    }

    return (
      <ShopPayIcon className="relative mx-auto inline-block h-auto w-22" />
    );
  }, [buttonText]);

  return (
    <Button
      className="w-pay-button border-none px-4 py-2-5 text-white"
      disabled={disabled}
      onClick={() => {
        if (!href) {
          return;
        }

        isoWindow.location.assign(href);
      }}
    >
      <span className="sr-only">{translate('shopPayButton.buy')}</span>
      {content}
    </Button>
  );
};

const getFeatureDictionary = async (locale: string) =>
  // Temporary adding an await here so that the i18n-dynamic-import-replacer
  // replaces the import statement with the correct file.
  // https://github.com/Shopify/shop-identity/issues/2814
  // eslint-disable-next-line no-return-await
  await import(`./translations/${locale}.json`);

register<PayButtonProps>(
  ({element, ...props}) => (
    <RootProvider
      element={element}
      featureName="ShopPayButton"
      getFeatureDictionary={getFeatureDictionary}
    >
      <ShopPayButtonInternal {...props} />
    </RootProvider>
  ),
  {
    name: 'shop-pay-button',
    props: {
      buttonText: 'string',
      channel: 'string',
      disabled: 'boolean',
      paymentOption: 'string',
      source: 'string',
      sourceToken: 'string',
      storeUrl: 'string',
      variants: 'string',
    },
    shadow: 'open',
  },
);

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace preact.JSX {
    interface IntrinsicElements {
      ['shop-pay-button']: PayButtonWebComponentProps;
    }
  }
}

export function ShopPayButton(props: PayButtonWebComponentProps) {
  return <shop-pay-button {...props} />;
}
